import Link from '@mui/material/Link';
import { Group, GroupType, OrganisationGroup } from 'api';
import { Link as RouterLink } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from '@mui/material';
import { TextV2 } from '@insights-ltd/design-library/components';

type Kind = 'orgCard' | 'subtitle' | 'bold';

type OrganisationGroupLinkProps = {
  group: Group | OrganisationGroup;
  i18nKey?: string | null;
  kind?: Kind;
};

const GroupData = styled('span', {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: Kind }>(({ theme, kind }) => {
  const classMap = {
    orgCard: {
      groupData: null,
    },
    bold: {
      groupData: {
        fontWeight: theme.typography.fontWeightBold,
      },
    },
    subtitle: {
      groupData: {
        color: theme.palette.text.secondary,
      },
    },
  };
  return classMap[kind].groupData;
});

const OrganisationGroupLink = ({
  group,
  i18nKey = 'ui.event-management.organisation.parent-group',
  kind = 'subtitle',
}: OrganisationGroupLinkProps) => {
  const { id, name } = group;
  const type = 'type' in group ? group.type : undefined;
  const { t } = useTranslation();
  const typeTranslations: Record<GroupType, string> = {
    CUSTOMER: t('ui.event-management.organisations.type.customer'),
    PARTNER: t('ui.event-management.organisations.type.partner'),
  };
  const groupTypeLabel = type ? typeTranslations[type] : undefined;

  return (
    <TextV2 component="p">
      <Trans
        i18nKey={i18nKey ?? undefined}
        values={{
          groupName: name,
          groupType: groupTypeLabel,
        }}
        components={{
          groupDataStart: <GroupData kind={kind} />,
          styledLink: (
            <Link
              sx={(theme) => ({
                color: theme.palette.blue.main,
                fontWeight: theme.typography.fontWeightBold,
                wordBreak: 'break-word',
                flex: '4',
                margin: '0.25rem',
              })}
              component={RouterLink}
              to={`/groups/${id}`}
            />
          ),
          groupDataEnd: <GroupData kind={kind} />,
          groupDataType: (
            <GroupData
              sx={{ marginLeft: '0.25rem', fontWeight: '600' }}
              kind={kind}
            />
          ),
        }}
      />
    </TextV2>
  );
};

export default OrganisationGroupLink;
